/**
 * Centralizes publisher-ledger status semantics so every live-state view presents
 * sync and inventory state consistently without duplicating backend-aware logic.
 */
import type {
  EntityStatus,
  PublisherEntityDto,
  PublisherEntityType,
  SyncStatus,
} from 'src/store/generated/leela-api-generated';

export enum PublisherEntityBadge {
  LIVE = 'LIVE',
  // PROCESSING = pushed/synced to the publisher (for ADSP: Dynamic Ads -> ACP done) but still
  // awaiting downstream review (for ADSP: ACP -> Rodeo pending). Distinct from PENDING (not yet pushed).
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
  REMOVED = 'REMOVED',
}

export const BADGE_LABELS: Record<PublisherEntityBadge, string> = {
  [PublisherEntityBadge.LIVE]: 'publisher_entity_status_live',
  [PublisherEntityBadge.PROCESSING]: 'publisher_entity_status_processing',
  [PublisherEntityBadge.PENDING]: 'publisher_entity_status_pending',
  [PublisherEntityBadge.FAILED]: 'publisher_entity_status_failed',
  [PublisherEntityBadge.SKIPPED]: 'publisher_entity_status_skipped',
  [PublisherEntityBadge.REMOVED]: 'publisher_entity_status_removed',
};

type PublisherEntityStatusFields = Pick<
PublisherEntityDto,
'publisherEntityType' | 'syncStatus' | 'entityStatus'
>;

function deriveSyncedStatus (
  publisherEntityType: PublisherEntityType,
  entityStatus: EntityStatus | undefined,
): PublisherEntityBadge {
  switch (publisherEntityType) {
    // ADSP CONCEPT/CREATIVE carry the same inventory semantics as AD/ASSET: once synced
    // their entityStatus tells us whether they are live or removed. Without these cases a
    // synced concept/creative fell through to the default and wrongly badged PENDING.
    case 'AD':
    case 'ASSET':
    case 'CONCEPT':
    case 'CREATIVE':
      if (entityStatus === 'IN_STOCK') return PublisherEntityBadge.LIVE;
      if (entityStatus === 'OUT_OF_STOCK') return PublisherEntityBadge.REMOVED;
      return PublisherEntityBadge.PENDING;
    // PRODUCT_SET is ADSP's structural CCG root; like CAMPAIGN/AD_GROUP it is live once synced.
    case 'CAMPAIGN':
    case 'AD_GROUP':
    case 'PRODUCT_SET':
      return PublisherEntityBadge.LIVE;
    default:
      return PublisherEntityBadge.PENDING;
  }
}

export function derivePublisherEntityStatus (
  entity: PublisherEntityStatusFields,
): PublisherEntityBadge {
  const syncStatus: SyncStatus | undefined = entity.syncStatus;

  switch (syncStatus) {
    case 'SYNCED':
      return deriveSyncedStatus(entity.publisherEntityType, entity.entityStatus);
    case 'FAILED':
      return PublisherEntityBadge.FAILED;
    case 'SKIPPED':
      return PublisherEntityBadge.SKIPPED;
    case 'PROCESSING':
      // Synced to the publisher (ADSP: Dynamic Ads -> ACP), still awaiting review (ADSP: ACP -> Rodeo).
      return PublisherEntityBadge.PROCESSING;
    case 'PENDING':
    default:
      return PublisherEntityBadge.PENDING;
  }
}

/** Sort order for entity status badges — LIVE first, then PENDING, FAILED, SKIPPED, REMOVED last. */
export const STATUS_SORT_ORDER: Record<PublisherEntityBadge, number> = {
  [PublisherEntityBadge.LIVE]: 0,
  [PublisherEntityBadge.PROCESSING]: 1,
  [PublisherEntityBadge.PENDING]: 2,
  [PublisherEntityBadge.FAILED]: 3,
  [PublisherEntityBadge.SKIPPED]: 4,
  [PublisherEntityBadge.REMOVED]: 5,
};

/** Comparator: sorts entities by derived status (LIVE first). */
export function compareByStatus (a: PublisherEntityStatusFields, b: PublisherEntityStatusFields): number {
  return (STATUS_SORT_ORDER[derivePublisherEntityStatus(a)] ?? 4) - (STATUS_SORT_ORDER[derivePublisherEntityStatus(b)] ?? 4);
}
